import { forwardRef } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import { SettingIcon, UserManagementIcon } from "@/assets/icons";
import Tooltip from "@/components/ui/tooltip";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { clearCurrRunningAnalysisId } from "@/store/slice/spatialSlice";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import { PopoverMenuProps, SidebarFooterPopoverType } from "../types";
import FooterItemWithPopover from "./FooterItemWithPopover";

const ICON_CLASSNAME = "w-5 h-5 fill-dim-gray";

type SettingMenuWrapperProps = {
  setActivePopover: (popoverType: SidebarFooterPopoverType) => void;
  activePopover?: SidebarFooterPopoverType;
  isPopoverTriggerOrigin: (e: Event) => boolean;
  sidebarMenu?: boolean;
};

export const SettingMenuWrapper = forwardRef<
  HTMLButtonElement,
  SettingMenuWrapperProps
>(
  (
    { activePopover, isPopoverTriggerOrigin, setActivePopover, sidebarMenu },
    ref
  ) => {
    return (
      <FooterItemWithPopover
        togglePopover={(open) => {
          setActivePopover(open ? "setting" : "");
        }}
        classNamePopoverContent={twMerge("mt-3", sidebarMenu && "ml-6")}
        isPopoverTriggerOrigin={isPopoverTriggerOrigin}
        popoverTrigger={
          <Button
            data-testid="setting-popover-trigger"
            ref={ref}
            className={twMerge(
              " p-0 rounded-lg hover:bg-teal-50",
              activePopover === "setting" && "bg-teal-50",
              sidebarMenu && "w-[231px] justify-start"
            )}
          >
            <Tooltip
              disable={sidebarMenu}
              content="Settings"
              position="right"
              key="Settings"
            >
              <div className="flex items-center justify-center">
                <SettingIcon
                  className={twMerge(
                    "fill-dim-gray hover:fill-teal-500 w-10 h-10 p-2",
                    activePopover === "setting" && "fill-teal-500"
                  )}
                />
                <span
                  className={twMerge(
                    "whitespace-pre text-sm font-medium",
                    !sidebarMenu &&
                      "opacity-0 translate-x-28 overflow-hidden hidden",
                    activePopover === "setting"
                      ? "text-black font-bold"
                      : "text-dim-gray"
                  )}
                >
                  Settings
                </span>
              </div>
            </Tooltip>
          </Button>
        }
        popoverContent={<SettingMenu setActivePopover={setActivePopover} />}
        isOpen={activePopover === "setting"}
      />
    );
  }
);

const SettingMenuItem = ({
  onClick,
  href,
  label,
  icon
}: {
  onClick: () => void;
  href: string;
  label: string;
  icon: React.ReactNode;
}) => (
  <Link
    to={href}
    onClick={() => {
      onClick();
      toast.dismiss();
    }}
    data-testid={`setting-menu-${label}`}
  >
    <Button className="w-full justify-start gap-2 hover:bg-neutral-100 p-2 rounded-xl">
      <div className="flex flex-1 gap-2">
        {icon}
        <div className="flex flex-col">
          <span className="text-sm">{label}</span>
        </div>
      </div>
    </Button>
  </Link>
);

const SettingMenu = ({ setActivePopover }: PopoverMenuProps) => {
  const dispatch = useDispatch();
  const { isGroupManagementEnabled } = useAppFeatures();
  const userPermissions = useGetUserPermission();
  const isUserManagementGranted =
    userPermissions[EModuleName.USER_MANAGEMENT][EModulePermission.VIEW];
  const isUserGroupManagementGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][EModulePermission.VIEW];

  const handleOnClick = () => {
    setActivePopover("");
    dispatch(clearCurrRunningAnalysisId());
  };

  return (
    <div className="flex flex-col gap-3 p-2">
      <div className="flex flex-col">
        {isUserManagementGranted && (
          <SettingMenuItem
            href="/user-management"
            onClick={handleOnClick}
            label="Users"
            icon={<UserManagementIcon className={ICON_CLASSNAME} />}
          />
        )}
        {isGroupManagementEnabled && isUserGroupManagementGranted && (
          <SettingMenuItem
            href="/group-management"
            onClick={handleOnClick}
            label="Group"
            icon={<UserManagementIcon className={ICON_CLASSNAME} />}
          />
        )}
      </div>
    </div>
  );
};

export default SettingMenuWrapper;
